/**
 * Settings panel for user preferences.
 */

import { useSettingsStore } from '../../stores/settingsStore';
import type { HexResolution } from '../../stores/settingsStore';
import { useAuthStore } from '../../stores/authStore';
import { useIsMobile } from '../../hooks/useIsMobile';

export interface SettingsPanelProps {
  /** Additional CSS class */
  className?: string;
}

interface ToggleSwitchProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

function ToggleSwitch({ label, description, checked, onChange, disabled = false }: ToggleSwitchProps) {
  return (
    <label
      className={`flex items-center justify-between cursor-pointer group ${
        disabled ? 'opacity-50 pointer-events-none' : ''
      }`}
    >
      <div>
        <span className="text-sm font-medium text-gray-800 dark:text-gray-100">{label}</span>
        <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
      </div>
      <div className="relative">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          className="sr-only peer"
        />
        <div className="w-11 h-6 bg-gray-200 dark:bg-gray-600 rounded-full peer peer-checked:bg-blue-600 peer-disabled:bg-gray-100 dark:peer-disabled:bg-gray-700 transition-colors" />
        <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full peer-checked:translate-x-5 transition-transform" />
      </div>
    </label>
  );
}

const HEX_RESOLUTION_OPTIONS: { value: HexResolution; label: string }[] = [
  { value: 6, label: 'City' },
  { value: 7, label: 'District' },
  { value: 8, label: 'Block' },
];

function ResolutionSwitcher({
  value,
  onChange,
}: {
  value: HexResolution;
  onChange: (v: HexResolution) => void;
}) {
  return (
    <div className="ml-0 mt-1">
      <div className="flex rounded-md bg-gray-100 dark:bg-gray-700 p-0.5">
        {HEX_RESOLUTION_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={`flex-1 px-2 py-1 text-xs rounded transition-colors ${
              value === opt.value
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm font-medium'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function SettingsPanel({ className = '' }: SettingsPanelProps) {
  const isMobile = useIsMobile();
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === 'admin';
  const isLoggedIn = !!user;
  const showSelectedV4Position = useSettingsStore((state) => state.showSelectedV4Position);
  const setShowSelectedV4Position = useSettingsStore((state) => state.setShowSelectedV4Position);
  const showFreshnessDots = useSettingsStore((state) => state.showFreshnessDots);
  const setShowFreshnessDots = useSettingsStore((state) => state.setShowFreshnessDots);
  const hexDensityEnabled = useSettingsStore((state) => state.hexDensityEnabled);
  const setHexDensityEnabled = useSettingsStore((state) => state.setHexDensityEnabled);
  const hexResolution = useSettingsStore((state) => state.hexResolution);
  const setHexResolution = useSettingsStore((state) => state.setHexResolution);
  const pollerBboxEnabled = useSettingsStore((state) => state.pollerBboxEnabled);
  const showPositionV1DA = useSettingsStore((state) => state.showPositionV1DA);
  const setShowPositionV1DA = useSettingsStore((state) => state.setShowPositionV1DA);
  const showPositionV4 = useSettingsStore((state) => state.showPositionV4);
  const setShowPositionV4 = useSettingsStore((state) => state.setShowPositionV4);
  const showPositionV5 = useSettingsStore((state) => state.showPositionV5);
  const setShowPositionV5 = useSettingsStore((state) => state.setShowPositionV5);
  const smoothAnimationEnabled = useSettingsStore((state) => state.smoothAnimationEnabled);
  const debugMode = useSettingsStore((state) => state.debugMode);
  const showPerformanceStats = useSettingsStore((state) => state.showPerformanceStats);
  const theme = useSettingsStore((state) => state.theme);
  const setPollerBboxEnabled = useSettingsStore((state) => state.setPollerBboxEnabled);
  const setSmoothAnimationEnabled = useSettingsStore((state) => state.setSmoothAnimationEnabled);
  const setDebugMode = useSettingsStore((state) => state.setDebugMode);
  const setShowPerformanceStats = useSettingsStore((state) => state.setShowPerformanceStats);
  const setTheme = useSettingsStore((state) => state.setTheme);

  // On mobile, show simplified settings (theme + admin overlays)
  if (isMobile) {
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 ${className}`}>
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">Settings</h2>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-gray-800 dark:text-gray-100">Theme</span>
              <p className="text-xs text-gray-500 dark:text-gray-400">App appearance</p>
            </div>
            <select
              value={theme}
              onChange={(e) => setTheme(e.target.value as 'light' | 'dark' | 'system')}
              className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg border-0 text-gray-900 dark:text-gray-100 min-h-[44px]"
            >
              <option value="system">System</option>
              <option value="light">Light</option>
              <option value="dark">Dark</option>
            </select>
          </div>
          {isAdmin && (
            <ToggleSwitch
              label="Hex density overlay"
              description="Show H3 hexagonal vehicle density heatmap"
              checked={hexDensityEnabled}
              onChange={setHexDensityEnabled}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 ${className}`}>
      <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">Settings</h2>

      {/* Display Settings */}
      <div className="space-y-3">
        {isAdmin && (
          <ToggleSwitch
            label="Show deviation line"
            description="Draw line between V4 estimated and V1-DA GPS positions"
            checked={showSelectedV4Position}
            onChange={setShowSelectedV4Position}
          />
        )}
        {isAdmin && (
          <ToggleSwitch
            label="Show freshness dots"
            description="Show per-source data freshness indicators on vehicle markers"
            checked={showFreshnessDots}
            onChange={setShowFreshnessDots}
          />
        )}
        {isAdmin && (
          <ToggleSwitch
            label="Hex density overlay"
            description="Show H3 hexagonal vehicle density heatmap"
            checked={hexDensityEnabled}
            onChange={setHexDensityEnabled}
          />
        )}
        {isAdmin && hexDensityEnabled && (
          <ResolutionSwitcher value={hexResolution} onChange={setHexResolution} />
        )}
        {isAdmin && (
          <ToggleSwitch
            label="Show poller bounding boxes"
            description="Display distributed poller coverage areas on the map"
            checked={pollerBboxEnabled}
            onChange={setPollerBboxEnabled}
          />
        )}

      </div>

      {/* Position Sources (logged-in users) */}
      {isLoggedIn && (
        <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
          <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">
            Position Sources
          </h3>
          <div className="space-y-3">
            <ToggleSwitch
              label="V1-DA GPS"
              description="Show V1-DA real GPS position crosshair"
              checked={showPositionV1DA}
              onChange={setShowPositionV1DA}
            />
            <ToggleSwitch
              label="V4 Estimated"
              description="Show V4 estimated position crosshair"
              checked={showPositionV4}
              onChange={setShowPositionV4}
            />
            <ToggleSwitch
              label="V5 Computed"
              description="Show V5 segment-interpolated position crosshair"
              checked={showPositionV5}
              onChange={setShowPositionV5}
            />
          </div>
        </div>
      )}

      {/* Developer Settings (admin only) */}
      {isAdmin && (
        <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
          <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">
            Developer
          </h3>
          <div className="space-y-3">
            <ToggleSwitch
              label="Debug mode"
              description="Enable developer debugging features"
              checked={debugMode}
              onChange={setDebugMode}
            />
            <ToggleSwitch
              label="Smooth vehicle animation"
              description="Animate vehicles smoothly between position updates"
              checked={smoothAnimationEnabled}
              onChange={setSmoothAnimationEnabled}
            />
            <ToggleSwitch
              label="Performance stats"
              description="Show render times and message counts"
              checked={showPerformanceStats}
              onChange={setShowPerformanceStats}
              disabled={!debugMode}
            />
          </div>
        </div>
      )}
    </div>
  );
}
